import type { JsonValue } from "./json";
import { PUBLIC_ERROR_CATALOG } from "./public-error-catalog";

export { PUBLIC_ERROR_CATALOG } from "./public-error-catalog";

export type PublicErrorCode = keyof typeof PUBLIC_ERROR_CATALOG;
export type PublicErrorLocale = "zh-CN" | "en";
export const PUBLIC_ERROR_CODES = Object.keys(PUBLIC_ERROR_CATALOG) as PublicErrorCode[];
export const PUBLIC_ERROR_MESSAGES: Record<PublicErrorLocale, Record<PublicErrorCode, string>> = {
  "zh-CN": Object.fromEntries(PUBLIC_ERROR_CODES.map(code => [code, PUBLIC_ERROR_CATALOG[code]["zh-CN"]])) as Record<PublicErrorCode, string>,
  en: Object.fromEntries(PUBLIC_ERROR_CODES.map(code => [code, PUBLIC_ERROR_CATALOG[code].en])) as Record<PublicErrorCode, string>,
};

export type PublicUpstreamUnavailableReason = "no_response" | "not_retained" | "unsupported_capture" | "error_not_emitted" | "size_limit";
export type PublicUpstreamError = {
  upstreamHttpStatus?: number;
  upstreamRequestId?: string;
  body?: JsonValue;
  redacted?: true;
  truncated?: true;
  unavailableReason?: PublicUpstreamUnavailableReason;
  format?: "sse" | "decoded";
  event?: string;
};
export type PublicErrorDetails = { param?: string };

/** Network errors retain unknown codes from older or newer servers. */
export type NexraError = { code: string; message: string; param?: string };
export type PublicErrorContext = { nexraRequestId?: string; nexraHttpStatus?: number };
export type PublicErrorFields = PublicErrorContext & {
  nexraError?: NexraError;
  upstreamHttpStatus?: number;
  upstreamRequestId?: string;
  upstreamError?: JsonValue;
  upstreamErrorMessage?: string;
  upstreamRedacted?: true;
  upstreamTruncated?: true;
  upstreamUnavailableReason?: PublicUpstreamUnavailableReason;
  upstreamFormat?: "sse" | "decoded";
  upstreamEvent?: string;
};
export type PublicError = PublicErrorFields & { nexraError: NexraError };

/** Platform HTTP facts belong to the request record; messages belong to the consumer locale. */
export type PublicErrorSnapshot = { code: PublicErrorCode; upstream?: PublicUpstreamError; details?: PublicErrorDetails };
export type ApiErrorPayload = PublicError;

export function isPublicErrorCode(code: string): code is PublicErrorCode {
  return Object.hasOwn(PUBLIC_ERROR_CATALOG, code);
}

export function publicErrorMessage(code: string, locale: PublicErrorLocale = "en"): string {
  return PUBLIC_ERROR_MESSAGES[locale][isPublicErrorCode(code) ? code : "internal_error"];
}

export function pickPublicErrorDetails(value: unknown): PublicErrorDetails | undefined {
  return isRecord(value) && typeof value.param === "string" && value.param.trim() ? { param: value.param } : undefined;
}

/** Picks wrapper metadata only; body is opaque evidence already secured by its capture owner. */
export function pickPublicUpstreamError(value: unknown): PublicUpstreamError | undefined {
  if (!isRecord(value)) return undefined;
  const upstreamHttpStatus = httpStatus(value.upstreamHttpStatus !== undefined ? value.upstreamHttpStatus : value.httpStatus);
  const upstreamRequestId = identifier(value.upstreamRequestId !== undefined ? value.upstreamRequestId : value.requestId);
  const hasBody = Object.hasOwn(value, "body") && value.body !== undefined;
  const format = value.format === "sse" || value.format === "decoded" ? value.format : undefined;
  const event = format === "sse" && typeof value.event === "string" && value.event ? value.event : undefined;
  const unavailableReason = hasBody ? undefined : upstreamUnavailableReason(value.unavailableReason);
  const upstream: PublicUpstreamError = {
    ...(upstreamHttpStatus !== undefined ? { upstreamHttpStatus } : {}),
    ...(upstreamRequestId !== undefined ? { upstreamRequestId } : {}),
    ...(hasBody ? { body: value.body as JsonValue } : {}),
    ...(value.redacted === true ? { redacted: true } : {}),
    ...(value.truncated === true ? { truncated: true } : {}),
    ...(format ? { format } : {}),
    ...(event ? { event } : {}),
    ...(unavailableReason ? { unavailableReason } : {}),
  };
  return Object.keys(upstream).length ? upstream : undefined;
}

function evidenceFields(input: Record<string, unknown>): Pick<PublicErrorSnapshot, "upstream" | "details"> {
  const details = pickPublicErrorDetails(input.details);
  const upstream = pickPublicUpstreamError(input.upstream !== undefined ? input.upstream : isRecord(input.details) ? input.details.upstream : undefined);
  return { ...(upstream ? { upstream } : {}), ...(details ? { details } : {}) };
}

function errorSource(input: Record<string, unknown>): Record<string, unknown> {
  return Object.hasOwn(input, "nexraError") ? input : isRecord(input.error) ? input.error : input;
}

function nexraErrorValue(source: Record<string, unknown>): unknown {
  return Object.hasOwn(source, "nexraError") ? source.nexraError : source;
}

function pickNexraError(source: Record<string, unknown>): NexraError | undefined {
  const value = nexraErrorValue(source);
  if (!isRecord(value)) return undefined;
  const code = identifier(value.code);
  if (!code || typeof value.message !== "string") return undefined;
  const param = identifier(value.param) ?? (Object.hasOwn(source, "nexraError") ? undefined : pickPublicErrorDetails(source.details)?.param);
  return { code, message: value.message, ...(param ? { param } : {}) };
}

/** Success and failure DTOs share one request context; diagnostic bodies never classify success output. */
export function pickPublicErrorFields(input: unknown, context: PublicErrorContext = {}): PublicErrorFields {
  const owner = isRecord(input) ? input : {};
  const source = errorSource(owner);
  const ownerId = identifier(owner.nexraRequestId !== undefined ? owner.nexraRequestId : owner.requestId);
  const sourceId = identifier(source.nexraRequestId !== undefined ? source.nexraRequestId : source.requestId);
  const originalId = ownerId ?? sourceId;
  const contextId = identifier(context.nexraRequestId);
  const nexraRequestId = originalId ?? contextId;
  const status = (ownerId ? httpStatus(owner.nexraHttpStatus) : undefined)
    ?? (sourceId && sourceId === originalId ? httpStatus(source.nexraHttpStatus) : undefined);
  // A query or callback delivery must not supply another request's HTTP result.
  const nexraHttpStatus = nexraRequestId ? status ?? (contextId === nexraRequestId ? httpStatus(context.nexraHttpStatus) : undefined) : undefined;
  const nexraError = pickNexraError(source);
  return {
    ...(nexraRequestId ? { nexraRequestId } : {}), ...(nexraHttpStatus !== undefined ? { nexraHttpStatus } : {}),
    ...(nexraError ? { nexraError } : {}),
    ...flatUpstreamFields(owner, source),
  };
}

/** Reads whole canonical or legacy owners without localizing or reclassifying their server error. */
export function normalizePublicErrorInput(input: unknown, context: PublicErrorContext = {}): PublicError | undefined {
  const fields = pickPublicErrorFields(input, context);
  return fields.nexraError ? { ...fields, nexraError: fields.nexraError } : undefined;
}

function flatUpstreamFields(owner: Record<string, unknown>, source: Record<string, unknown>): PublicErrorFields {
  const legacy = evidenceFields(source).upstream;
  const field = (key: keyof PublicErrorFields, fallback?: unknown): unknown => Object.hasOwn(owner, key) ? owner[key] : Object.hasOwn(source, key) ? source[key] : fallback;
  // Canonical values are already extracted. Only a legacy captured body loses its first error wrapper.
  const body = Object.hasOwn(owner, "upstreamError") ? owner.upstreamError
    : Object.hasOwn(source, "upstreamError") ? source.upstreamError
      : legacy?.body !== null && typeof legacy?.body === "object" && Object.hasOwn(legacy.body, "error")
        ? (legacy.body as Record<string, JsonValue>).error : legacy?.body;
  const upstream = pickPublicUpstreamError({
    upstreamHttpStatus: field("upstreamHttpStatus", legacy?.upstreamHttpStatus),
    upstreamRequestId: field("upstreamRequestId", legacy?.upstreamRequestId),
    ...(body !== undefined ? { body } : {}),
    redacted: field("upstreamRedacted", legacy?.redacted),
    truncated: field("upstreamTruncated", legacy?.truncated),
    format: field("upstreamFormat", legacy?.format),
    event: field("upstreamEvent", legacy?.event),
    unavailableReason: field("upstreamUnavailableReason", legacy?.unavailableReason),
  });
  const explicitMessage = field("upstreamErrorMessage");
  const upstreamErrorMessage = typeof explicitMessage === "string" && explicitMessage.trim()
    ? explicitMessage : extractUpstreamErrorMessage(upstream?.body);
  return {
    ...(upstreamErrorMessage !== undefined ? { upstreamErrorMessage } : {}),
    ...(upstream?.upstreamHttpStatus !== undefined ? { upstreamHttpStatus: upstream.upstreamHttpStatus } : {}),
    ...(upstream?.upstreamRequestId ? { upstreamRequestId: upstream.upstreamRequestId } : {}),
    ...(upstream && Object.hasOwn(upstream, "body") ? { upstreamError: upstream.body } : {}),
    ...(upstream?.redacted ? { upstreamRedacted: true } : {}),
    ...(upstream?.truncated ? { upstreamTruncated: true } : {}),
    ...(upstream?.format ? { upstreamFormat: upstream.format } : {}),
    ...(upstream?.event ? { upstreamEvent: upstream.event } : {}),
    ...(upstream?.unavailableReason ? { upstreamUnavailableReason: upstream.unavailableReason } : {}),
  };
}

function upstreamUnavailableReason(value: unknown): PublicUpstreamUnavailableReason | undefined {
  return value === "no_response" || value === "not_retained" || value === "unsupported_capture" || value === "error_not_emitted" || value === "size_limit" ? value : undefined;
}

/** Old snapshots are projected on read; their stored bodies and locale-independent facts are not rewritten. */
export function normalizePublicErrorSnapshot(input: unknown): PublicErrorSnapshot | undefined {
  if (!isRecord(input) || typeof input.code !== "string" || !isPublicErrorCode(input.code)) return undefined;
  return { code: input.code, ...evidenceFields(input) };
}

export function buildPublicError(snapshot: PublicErrorSnapshot, locale: PublicErrorLocale = "en", nexraRequestId?: string, nexraHttpStatus?: number): PublicError {
  const fields = pickPublicErrorFields(evidenceFields(snapshot), { nexraRequestId, nexraHttpStatus });
  const nexraError: NexraError = { code: snapshot.code, message: publicErrorExplanation(snapshot.code, locale, fields.upstreamErrorMessage), ...pickPublicErrorDetails(snapshot.details) };
  return { ...fields, nexraError };
}

export function localizePublicError(error: PublicError | { code: string; message?: string; param?: string; requestId?: string; nexraRequestId?: string; nexraHttpStatus?: number; upstream?: unknown; details?: unknown }, locale: PublicErrorLocale = "en"): PublicError {
  const code = "nexraError" in error ? error.nexraError.code : error.code;
  const param = "nexraError" in error ? identifier(error.nexraError.param) : identifier(error.param) ?? pickPublicErrorDetails(error.details)?.param;
  const fields = pickPublicErrorFields(error);
  return { ...fields, nexraError: { code, message: publicErrorExplanation(code, locale, fields.upstreamErrorMessage), ...(param ? { param } : {}) } };
}

/** Extracts text only from known provider error fields, never arbitrary metadata. */
export function extractUpstreamErrorMessage(value: unknown): string | undefined {
  const visited = new WeakSet<object>();
  const read = (candidate: unknown, depth: number): string | undefined => {
    if (typeof candidate === "string") return candidate.trim() ? candidate : undefined;
    if (depth > 8 || !candidate || typeof candidate !== "object" || visited.has(candidate)) return undefined;
    visited.add(candidate);
    if (Array.isArray(candidate)) {
      const messages = candidate.flatMap(item => read(item, depth + 1) ?? []);
      return messages.length ? messages.join("\n") : undefined;
    }
    const record = candidate as Record<string, unknown>;
    for (const key of ["error", "Error"] as const) {
      const message = read(record[key], depth + 1);
      if (message) return message;
    }
    for (const key of ["errorMessage", "failure_reason", "status_message", "errMsg"] as const) {
      const message = record[key];
      if (typeof message === "string" && message.trim()) return message;
    }
    for (const key of ["base_resp", "detail", "output", "task", "data", "response", "Response", "videoGenerateTaskInfo", "steps"] as const) {
      if (key === "base_resp" && isRecord(record[key]) && record[key].status_code === 0) continue;
      const message = read(record[key], depth + 1);
      if (message) return message;
    }
    for (const key of ["message", "msg", "status_msg"] as const) {
      const message = record[key];
      if (typeof message === "string" && message.trim()) return message;
    }
    if (Array.isArray(record.candidates)) {
      const messages = record.candidates.flatMap(candidate => {
        if (!isRecord(candidate)) return [];
        const parts = isRecord(candidate.content) ? candidate.content.parts : undefined;
        const texts = Array.isArray(parts) ? parts.flatMap(part =>
          isRecord(part) && part.thought !== true && typeof part.text === "string" && part.text.trim() ? [part.text] : []) : [];
        return texts.length ? texts : typeof candidate.finishMessage === "string" && candidate.finishMessage.trim() ? [candidate.finishMessage] : [];
      });
      if (messages.length) return messages.join("\n");
    }
    if (isRecord(record.promptFeedback)) {
      const message = record.promptFeedback.blockReasonMessage;
      if (typeof message === "string" && message.trim()) return message;
    }
    return undefined;
  };
  return read(value, 0);
}

function publicErrorExplanation(code: string, locale: PublicErrorLocale, upstreamErrorMessage: string | undefined): string {
  const summary = publicErrorMessage(code, locale);
  const reason = upstreamErrorMessage?.trim();
  if (!reason || reason === summary) return summary;
  const bounded = reason.length > 2000 ? `${reason.slice(0, 2000)}…` : reason;
  return `${summary}\n${locale === "zh-CN" ? "具体原因" : "Details"}: ${bounded}`;
}

function identifier(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function httpStatus(value: unknown): number | undefined {
  return typeof value === "number" && Number.isInteger(value) && value >= 100 && value <= 599 ? value : undefined;
}

export function resolvePublicErrorLocale(acceptLanguage?: string | null): PublicErrorLocale {
  let chosen: PublicErrorLocale = "en";
  let highestQuality = 0;
  for (const candidate of (acceptLanguage ?? "").split(",")) {
    const [tag, ...parameters] = candidate.trim().toLowerCase().split(";");
    const locale = /^zh(?:-[a-z0-9]+)*$/.test(tag ?? "") ? "zh-CN" : /^en(?:-[a-z0-9]+)*$/.test(tag ?? "") ? "en" : undefined;
    if (!locale) continue;
    const qualityParameter = parameters.map(parameter => parameter.trim()).find(parameter => parameter.startsWith("q="));
    const rawQuality = qualityParameter?.slice(2).trim();
    const quality = rawQuality === undefined ? 1 : /^(?:0(?:\.\d{0,3})?|1(?:\.0{0,3})?)$/.test(rawQuality) ? Number(rawQuality) : 0;
    if (quality > highestQuality) { chosen = locale; highestQuality = quality; }
  }
  return chosen;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
